import type { PostHogConfig } from 'posthog-js/dist/module.slim.no-external';

import { maskReplayAttribute } from '@/usage/replayMask';
import { type ToPattern, scrubUrl } from '@/usage/scrub';

/**
 * Session replay of usage analytics (owner 2026-10-15): masked, behind its
 * own switch `VITE_POSTHOG_REPLAY=on` (usage/config.ts), and only when usage
 * analytics itself is on. Everything here is global; no screen needs work.
 *
 * - all text is masked (`maskTextSelector: '*'`) and every input value;
 * - attributes that can carry text, names or addresses (title, alt,
 *   aria-label, placeholder, href, src, data-*, id, ...) are masked; only
 *   layout attributes are kept, and `url(...)` in inline styles is removed;
 * - images, canvas, SVG (amCharts, icons), video, iframes and hidden / file
 *   inputs are blocked: the replay shows a placeholder of the same size;
 * - no network recording, no console logs, no canvas, no cross-origin iframes;
 * - page addresses in the replay pass the same route-pattern filter as the
 *   page views (`/brandprofile/:id`, no query string);
 * - a recording shorter than about 5 s is not sent.
 *
 * The recorder (rrweb) is bundled with the app: `loadReplay` imports it
 * (its own chunk) only when replay is on. It is never loaded from PostHog's
 * domain; recordings go to our same-origin `/ingest` path like the events.
 * The replay settings are given to the SDK locally (`replayRemoteConfig`),
 * so no configuration is fetched from PostHog and a project setting can not
 * unmask anything.
 */

/** Elements recorded only as a placeholder of the same size. */
export const REPLAY_BLOCK_SELECTOR = [
  'img',
  'picture',
  'video',
  'audio',
  'canvas',
  'svg',
  'iframe',
  'object',
  'embed',
  'input[type="hidden"]',
  'input[type="file"]',
].join(', ');

export { MASKED_ATTRIBUTE, maskReplayAttribute } from '@/usage/replayMask';

/** A page or request address as it may appear in a replay; null = left out. */
export const replayUrl = (url: unknown, toPattern: ToPattern): string | null =>
  scrubUrl(url, toPattern)?.url ?? null;

export const REPLAY_MINIMUM_DURATION_MS = 5000;

/**
 * The SDK options for replay. One place, so the tests pin every masking setting.
 * `persistence`, `ip`, `respect_dnt` and the rest stay as in `usageInitOptions`.
 */
export const usageReplayOptions = (toPattern: ToPattern): Partial<PostHogConfig> => ({
  disable_session_recording: false,
  enable_recording_console_log: false,
  session_recording: {
    maskAllInputs: true,
    maskInputOptions: { password: true },
    maskTextSelector: '*',
    blockSelector: REPLAY_BLOCK_SELECTOR,
    maskAttributeFn: maskReplayAttribute,
    recordHeaders: false,
    recordBody: false,
    recordCrossOriginIframes: false,
    captureCanvas: { recordCanvas: false },
    collectFonts: false,
    captureJsonLd: false,
    // uncompressed in memory, so the e-mail / token guard of usage/scrub.ts sees
    // the replay data (the request itself is still compressed)
    compress_events: false,
    // page addresses (meta, page views, navigation) through the route-pattern filter
    maskCapturedNetworkRequestFn: (request) => {
      const name = replayUrl(request.name ?? (request as { url?: string }).url, toPattern);
      return name ? { ...request, name } : null;
    },
  },
});

/**
 * The replay part of the configuration the SDK would otherwise fetch from
 * PostHog. Given locally: no network capture, no console, no canvas, the
 * minimum duration, and the path the recordings are sent to (below the host).
 */
export const replayRemoteConfig = () => ({
  supportedCompression: ['gzip-js', 'base64'],
  capturePerformance: false,
  hasFeatureFlags: false,
  sessionRecording: {
    endpoint: '/s/',
    consoleLogRecordingEnabled: false,
    recordCanvas: false,
    minimumDurationMilliseconds: REPLAY_MINIMUM_DURATION_MS,
    networkPayloadCapture: { recordHeaders: false, recordBody: false },
  },
});

export type ReplayExtensions = NonNullable<PostHogConfig['__extensionClasses']>;

interface RemoteConfigWindow {
  _POSTHOG_REMOTE_CONFIG?: Record<string, { config: unknown }>;
}

/**
 * Loads the bundled recorder and the replay extension (own chunks), and hands
 * the replay configuration to the SDK for project key `key`. Call before `init`.
 */
export const loadReplay = async (key: string): Promise<ReplayExtensions> => {
  const [, extensions] = await Promise.all([
    import('posthog-js/dist/posthog-recorder'),
    import('posthog-js/dist/extension-bundles'),
  ]);
  const target = window as unknown as RemoteConfigWindow;
  target._POSTHOG_REMOTE_CONFIG = {
    ...target._POSTHOG_REMOTE_CONFIG,
    [key]: { config: replayRemoteConfig() },
  };
  return { sessionRecording: extensions.SessionReplayExtensions.sessionRecording };
};
